import React, { createContext, useContext, useState, useCallback } from 'react';

interface TempCompanyData {
  companyName: string;
  category?: string;
  companySize?: string;
}

interface TempUserData {
  firstName: string;
  lastName: string;
  title?: string;
}

interface TempAuthData {
  company: TempCompanyData;
  user: TempUserData;
}

interface TempAuthDataContextType {
  tempData: TempAuthData;
  updateCompanyData: (data: Partial<TempCompanyData>) => void;
  updateUserData: (data: Partial<TempUserData>) => void;
  clearTempData: () => void;
}

const TempAuthDataContext = createContext<TempAuthDataContextType | undefined>(undefined);

const initialData: TempAuthData = {
  company: {
    companyName: ''
  },
  user: {
    firstName: '',
    lastName: ''
  }
};

export const TempAuthDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tempData, setTempData] = useState<TempAuthData>(initialData);

  const updateCompanyData = useCallback((data: Partial<TempCompanyData>) => {
    setTempData(prev => ({
      ...prev,
      company: { ...prev.company, ...data }
    }));
  }, []);

  const updateUserData = useCallback((data: Partial<TempUserData>) => {
    setTempData(prev => ({
      ...prev,
      user: { ...prev.user, ...data }
    }));
  }, []);

  const clearTempData = useCallback(() => {
    setTempData(initialData);
  }, []);

  const value = {
    tempData,
    updateCompanyData,
    updateUserData,
    clearTempData
  };

  return (
    <TempAuthDataContext.Provider value={value}>
      {children}
    </TempAuthDataContext.Provider>
  );
};

export const useTempAuthData = () => {
  const context = useContext(TempAuthDataContext);
  if (context === undefined) {
    throw new Error('useTempAuthData must be used within a TempAuthDataProvider');
  }
  return context;
};